"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useAuth } from "@/components/auth/Auth";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiRepRating } from "@/generated/models/ApiRepRating";
import { commonApiFetch } from "@/services/api/common-api";
import { MEMES_NOMINEE_CATEGORY, MEMES_NOMINEE_REQUIRED_REP } from "./memesNomination.constants";
const REP_RATING_STALE_TIME_MS = 30_000;
export interface NominationProgress {
  readonly currentRep: number;
  readonly remainingRep: number;
  readonly percent: number;
}
interface UseMemesNomineeProgressResult {
  readonly hasProfile: boolean;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly progress: NominationProgress | null;
}
const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const getProfileIdentity = (profile: ApiIdentity | null): string | null => {
  if (!profile) {
    return null;
  }
  const handle = profile.handle?.trim();
  if (handle !== undefined && handle.length > 0) {
    return handle;
  }
  const primaryWallet = profile.primary_wallet.trim();
  if (primaryWallet.length > 0) {
    return primaryWallet;
  }
  const query = profile.query?.trim();
  if (query !== undefined && query.length > 0) {
    return query;
  }
  return null;
};
const getNominationProgress = (currentRep: number | null | undefined, requiredRep = MEMES_NOMINEE_REQUIRED_REP): NominationProgress => {
  const finiteCurrentRep = typeof currentRep === "number" && Number.isFinite(currentRep) ? currentRep : 0;
  const finiteRequiredRep = Number.isFinite(requiredRep) && requiredRep > 0 ? requiredRep : MEMES_NOMINEE_REQUIRED_REP;
  return {
    currentRep: finiteCurrentRep,
    remainingRep: Math.max(0, finiteRequiredRep - finiteCurrentRep),
    percent: clamp(finiteCurrentRep / finiteRequiredRep * 100, 0, 100)
  };
};
export function useMemesNomineeProgress() {
  const $ = _c(18);
  const {
    connectedProfile,
    fetchingProfile
  } = useAuth();
  let profileIdentity;
  let t0;
  if ($[0] !== connectedProfile) {
    profileIdentity = getProfileIdentity(connectedProfile);
    t0 = profileIdentity?.toLowerCase() ?? null;
    $[0] = connectedProfile;
    $[1] = profileIdentity;
    $[2] = t0;
  } else {
    profileIdentity = $[1];
    t0 = $[2];
  }
  const profileIdentityQueryKey = t0;
  const hasProfile = !!profileIdentity;
  let t1;
  if ($[3] !== profileIdentityQueryKey) {
    t1 = [QueryKey.PROFILE_REP_RATINGS, {
      handleOrWallet: profileIdentityQueryKey,
      category: MEMES_NOMINEE_CATEGORY
    }];
    $[3] = profileIdentityQueryKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== profileIdentity) {
    t2 = async () => await commonApiFetch({
      endpoint: `profiles/${encodeURIComponent(profileIdentity)}/rep/rating`,
      params: {
        category: MEMES_NOMINEE_CATEGORY
      }
    });
    $[5] = profileIdentity;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== hasProfile || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled: hasProfile,
      staleTime: REP_RATING_STALE_TIME_MS
    };
    $[7] = hasProfile;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data,
    isError,
    isFetching
  } = useQuery(t3);
  let t4;
  bb0: {
    if (!hasProfile || !data) {
      t4 = null;
      break bb0;
    }
    let t5;
    if ($[11] !== data.rating) {
      t5 = getNominationProgress(data.rating);
      $[11] = data.rating;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = t5;
  }
  const progress = t4;
  const t5 = fetchingProfile || hasProfile && isFetching && !data;
  let t6;
  if ($[13] !== hasProfile || $[14] !== isError || $[15] !== progress || $[16] !== t5) {
    t6 = {
      hasProfile,
      isError,
      isLoading: t5,
      progress
    };
    $[13] = hasProfile;
    $[14] = isError;
    $[15] = progress;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}